import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Landing from './pages/Landing';
import { getGebieden, getStyling, getDeelgebieden, activeerGeplande } from './api/api';
import Houders from './pages/Houders';
import HouderDetail from './pages/HouderDetail';
import VergunningNieuw from './pages/VergunningNieuw';
import HouderNieuw from './pages/HouderNieuw';
import InrichtingDetail from './pages/InrichtingDetail';
import MeldingVerwerken from './pages/MeldingVerwerken';
import WijzigingsbesluitPagina from './pages/WijzigingsbesluitPagina';
import KlaarstaandModal from './pages/KlaarstaandModal';
import InrichtingHistorieModal from './pages/InrichtingHistorieModal';
import Inrichtingen from './pages/Inrichtingen';
import Putten from './pages/Putten';
import PutDetail from './pages/PutDetail';
import PutNieuw from './pages/PutNieuw';
import Veldwerk from './pages/Veldwerk';
import BulkImport from './pages/BulkImport';
import Beheertools from './pages/Beheertools';
import KaartStijlen from './pages/KaartStijlen';
import ProductieUitnodigingen from './pages/ProductieUitnodigingen';
import ProductieOverzicht from './pages/ProductieOverzicht';
import Login from './pages/Login';
import Account from './pages/Account';
import Gebruikers from './pages/Gebruikers';
import { AuthProvider, useAuth } from './components/AuthContext';

// Helpers voor legacy redirects met ID-param (React Router 6 heeft geen
// native :param-substitutie in path-attr van een Navigate-element).
/* Doorverwijzingen voor oude bookmarks.

   Deze lazen het id eerder uit window.location. Dat gaat mis zodra React de
   component nog één keer rendert nadat de URL al veranderd is: dan wordt het id
   uit de níeuwe URL geplukt en verwijst hij nog een keer door, naar een pad dat
   nergens op slaat. Met useParams komt het id uit de route die daadwerkelijk
   matchte, en dat verandert niet meer. */
function LegacyRedirect({ prefix }) {
  const params = useParams();
  const id = params.id ?? Object.values(params)[0];
  return <Navigate to={`${prefix}/${id}`} replace />;
}
function LegacyHouderInrichting() {
  // Welk soort recht het wordt kies je nu op de houderpagina zelf.
  const { houderId } = useParams();
  return <Navigate to={`/beregening/houders/${houderId}`} replace />;
}

// Banner rechtsonder tijdens preload van kaartoverlays.
function PreloadBanner() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        background: 'rgba(33, 33, 33, 0.92)',
        color: '#fff',
        fontSize: 13,
        padding: '10px 14px',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          width: 12,
          height: 12,
          border: '2px solid rgba(255,255,255,0.3)',
          borderTopColor: '#fff',
          borderRadius: '50%',
          animation: 'app-spin 0.9s linear infinite',
          display: 'inline-block',
        }}
      />
      Kaartoverlays laden…
      <style>{`@keyframes app-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Alle routes van de app, gerenderd binnen <main>.
function AppRoutes({ isLanding }) {
  return (
    <main className={isLanding ? 'main main-landing' : 'main'}>
      <Routes>
        {/* Landing-page (doel-keuze) */}
        <Route path="/" element={<Landing />} />

        {/* Beregening-module — alle huidige functionaliteit zit hieronder */}
        <Route path="/beregening" element={<Navigate to="/beregening/houders" replace />} />
        <Route path="/beregening/houders" element={<Houders />} />
        <Route path="/beregening/houders/nieuw" element={<HouderNieuw />} />
        <Route path="/beregening/houders/:id" element={<HouderDetail />} />
        {/* Een melding hoort bij een houder, niet per se bij een bestaande
            inrichting: de eerste melding van een houder heeft er nog geen. */}
        <Route path="/beregening/houders/:houderId/melding-verwerken" element={<MeldingVerwerken />} />
        <Route path="/beregening/houders/:houderId/vergunning-nieuw" element={<VergunningNieuw />} />
        {/* De oude gecombineerde route: welk soort recht het wordt kies je nu op
            de houderpagina, dus daar zetten we een bookmark af. */}
        <Route path="/beregening/houders/:houderId/inrichting-nieuw" element={<LegacyHouderInrichting />} />
        <Route path="/beregening/inrichtingen" element={<Inrichtingen />} />
        <Route path="/beregening/inrichtingen/:id/melding-verwerken" element={<MeldingVerwerken />} />
        <Route path="/beregening/inrichtingen/:id/wijzigingsbesluit" element={<WijzigingsbesluitPagina />} />
        {/* Met een gepland_id erachter bewerk je een besluit dat al klaarstaat.
            Zonder schuif je er eentje achteraan in de rij. */}
        <Route path="/beregening/inrichtingen/:id/wijzigingsbesluit/:geplandId" element={<WijzigingsbesluitPagina />} />
        <Route path="/beregening/inrichtingen/:id" element={<InrichtingDetail />}>
          <Route path="historie/:periode_id" element={<InrichtingHistorieModal />} />
          {/* Wat nog niet geldt opent hetzelfde soort venster als wat al gold. */}
          <Route path="gepland-besluit/:geplandId" element={<KlaarstaandModal soort="besluit" />} />
          <Route path="gepland-melding/:geplandId" element={<KlaarstaandModal soort="melding" />} />
        </Route>
        <Route path="/beregening/putten" element={<Putten />} />
        {/* Vóór /:id, zodat "nieuw" niet als putnummer wordt gelezen. React
            Router zet een vaste tekst sowieso boven een parameter, maar zo
            staat het ook in de leesvolgorde goed. */}
        <Route path="/beregening/putten/nieuw" element={<PutNieuw />} />
        <Route path="/beregening/putten/:id" element={<PutDetail />} />
        <Route path="/beregening/toezicht" element={<Veldwerk />} />

        {/* Beheertools — waterschap-breed, blijft buiten /beregening */}
        <Route path="/beheertools" element={<Beheertools />} />
        <Route path="/beheertools/bulk-import" element={<BulkImport />} />
        <Route path="/beheertools/kaart-stijlen" element={<KaartStijlen />} />
        <Route path="/beheertools/productie-uitnodigingen" element={<ProductieUitnodigingen />} />
        <Route path="/beheertools/productie-overzicht" element={<ProductieOverzicht />} />

        <Route path="/account" element={<Account />} />
        <Route path="/beheer/gebruikers" element={<Gebruikers />} />

        {/* Legacy redirects — voor bookmarks van vóór de portal-refactor */}
        <Route path="/houders" element={<Navigate to="/beregening/houders" replace />} />
        <Route path="/houders/nieuw" element={<Navigate to="/beregening/houders/nieuw" replace />} />
        <Route path="/houders/:id" element={<LegacyRedirect prefix="/beregening/houders" />} />
        <Route path="/houders/:houderId/inrichting-nieuw" element={<LegacyHouderInrichting />} />
        <Route path="/inrichtingen/:id" element={<LegacyRedirect prefix="/beregening/inrichtingen" />} />
        <Route path="/putten" element={<Navigate to="/beregening/putten" replace />} />
        <Route path="/putten/:id" element={<LegacyRedirect prefix="/beregening/putten" />} />
        <Route path="/toezicht" element={<Navigate to="/beregening/toezicht" replace />} />
        <Route path="/veldwerk" element={<Navigate to="/beregening/toezicht" replace />} />
        {/* De losse import-beheertool is vervallen: een melding verwerk je nu
            vanaf de houder, waar de bewaking op dubbele inrichtingen zit en waar
            een put uit een bijlage alleen bij een exacte coördinaat gekoppeld
            wordt. De routes blijven bestaan voor bookmarks. */}
        <Route path="/import-melding" element={<Navigate to="/beheertools" replace />} />
        <Route path="/beheertools/import-melding" element={<Navigate to="/beheertools" replace />} />
        <Route path="/bulk-import" element={<Navigate to="/beheertools/bulk-import" replace />} />
      </Routes>
    </main>
  );
}

// Hoofd-component: bepaalt layout (met of zonder sidebar) + start preload zodra
// er een module is gekozen (niet op de landing-page zelf, want daar is nog
// geen kaart in beeld).
function AppShell() {
  const location = useLocation();
  const isLanding = location.pathname === '/';

  const [overlaysLaden, setOverlaysLaden] = useState(false);

  useEffect(() => {
    if (isLanding) return; // op landing geen kaart-preload nodig
    setOverlaysLaden(true);
    // Activeer geplande wijzigingen waarvan ingangsdatum bereikt is (silent fail OK)
    activeerGeplande().catch(() => {});
    Promise.all([
      getGebieden(undefined, true),
      getStyling(),
      getDeelgebieden(),
    ])
      .then(() => setOverlaysLaden(false))
      .catch(err => {
        console.warn('Preload (gebieden of styling) mislukt — pages zullen zelf opnieuw fetchen:', err);
        setOverlaysLaden(false);
      });
  }, [isLanding]);

  if (isLanding) {
    return <AppRoutes isLanding />;
  }

  return (
    <div className="layout">
      <Sidebar />
      <AppRoutes isLanding={false} />
      {overlaysLaden && <PreloadBanner />}
    </div>
  );
}

// Toegangspoort: zolang we de inlogstatus ophalen tonen we een spinner; is er
// geen ingelogde gebruiker, dan de inlogpagina (alles dicht, ook de landing);
// pas bij een geldige sessie de app zelf.
function Gate() {
  const { gebruiker, laden } = useAuth();
  if (laden) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" />
      </div>
    );
  }
  if (!gebruiker) return <Login />;
  return <AppShell />;
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </Router>
  );
}
